import React, { useState, useEffect } from "react";
import {
  Box,
  FormControlLabel,
  FormControl,
  RadioGroup,
  FormGroup,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import { Permission, Document, DocumentType } from "protogen/documents_pb";
import Checkbox from "../common/Checkbox";
import Radio from "../common/Radio";
import { useSnackpack } from "../context/SnackpackContextProvider";
import FlexBox from "components/common/FlexBox";
import AddressAutocomplete, { Location } from "../common/AddressAutocomplete";
import ConfirmationDialog, {
  useConfirmationDialog,
} from "../common/ConfirmationDialog";
import FormHeader from "./FormHeader";
import { AccountStub, UploadAttachment } from "protogen/common_pb";
import { PlainMessage } from "@bufbuild/protobuf";
import { useUploader } from "../creation/FileUploader";
import UploadThumbnail from "../activity/UploadThumbnail";
import DragNDrop from "../common/DragNDrop";
import { Paperclip, Copy } from "lucide-react";
import { Attachment } from "protogen/common_pb";
import TextField from "../common/TextField";
import { useContext } from "react";
import { CurrentUserContext } from "../context/RequireAuth";
import { useCopyDocument } from "../../services/documents";
import { CopyDocumentRequest } from "protogen/documents_service_pb";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isUserAdvisor } from "../../common/userUtils";
import useIsMobile from "components/hooks/useIsMobile";

export interface BaseField {
  id: string;
  type: string;
  text: string;
  subtext?: string;
  value?: any;
  required?: boolean;
}

export interface TextField extends BaseField {
  type: "text";
  multiline?: boolean;
  value?: string;
}

export interface Option {
  value: string;
  label: string;
}

export interface RadioField extends BaseField {
  type: "radio";
  options: Option[];
  value?: string;
}

export interface CheckboxField extends BaseField {
  type: "checkbox";
  options: Option[];
  maxSelect?: number;
  value?: string[];
}

export interface LocationField extends BaseField {
  type: "location";
  value?: {
    latitude: number;
    longitude: number;
    address: string;
    unit: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    formattedAddress: string;
  };
}

export interface AttachmentField extends BaseField {
  type: "attachment";
  value: Attachment[];
  maxFiles?: number;
}

export type FormField =
  | TextField
  | RadioField
  | CheckboxField
  | LocationField
  | AttachmentField;

export interface FormStructure {
  title: string;
  description: string;
  fields: FormField[];
}

interface FormTextFieldProps extends Omit<TextField, "type"> {
  onChange: (value: string) => void;
  onBlur?: () => void;
  isReadOnly: boolean;
  required?: boolean;
  showError?: boolean;
}

const FormTextField = ({
  text,
  multiline,
  value = "",
  onChange,
  onBlur,
  subtext,
  isReadOnly,
  required,
  showError = false,
}: FormTextFieldProps): JSX.Element => {
  return (
    <FlexBox direction="column" sx={{ gap: "4px", marginTop: "0" }}>
      {text && (
        <Typography variant="h5">
          {text}
          {required && <span style={{ color: "#C14743" }}>*</span>}
        </Typography>
      )}
      {subtext && <Typography variant="bodySmall">{subtext}</Typography>}
      <TextField
        fullWidth
        multiline={multiline || isReadOnly ? true : false}
        rows={isReadOnly ? undefined : multiline ? 4 : 1}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value)
        }
        onBlur={onBlur}
        variant="detachedOutlined"
        margin="normal"
        disabled={isReadOnly}
        required={required}
        error={showError && required && (!value || value.trim() === "")}
        sx={{
          marginTop: "8px",
          ...(isReadOnly && {
            textarea: {
              "&.Mui-disabled": {
                WebkitTextFillColor: "rgba(0, 0, 0, 0.7)",
              },
              margin: "12px 0",
            },
          }),
        }}
      />
    </FlexBox>
  );
};

interface FormRadioProps extends Omit<RadioField, "type"> {
  onChange: (value: string) => void;
  onBlur?: () => void;
  isReadOnly: boolean;
  showError?: boolean;
}

const FormRadio = ({
  text,
  options,
  value = "",
  onChange,
  onBlur,
  subtext,
  isReadOnly,
  required,
  showError = false,
}: FormRadioProps) => {
  return (
    <FormControl
      component="fieldset"
      margin="normal"
      error={showError && required && (!value || value === "")}
      sx={{ gap: "4px", marginTop: "0" }}
    >
      <Typography variant="h5">
        {text}
        {required && <span style={{ color: "#C14743" }}>*</span>}
      </Typography>
      {subtext && <Typography variant="bodySmall">{subtext}</Typography>}
      <RadioGroup
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      >
        {options.map((option: Option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio />}
            label={option.label}
            disabled={isReadOnly}
            sx={{
              ...(isReadOnly && {
                "&.Mui-disabled": {
                  WebkitTextFillColor: "rgba(0, 0, 0, 0.7)",
                },
              }),
            }}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};

interface FormCheckboxProps extends Omit<CheckboxField, "type"> {
  onChange: (value: string[]) => void;
  onBlur?: () => void;
  isReadOnly: boolean;
  showError?: boolean;
}

const FormCheckbox = ({
  text,
  options,
  maxSelect,
  value = [],
  onChange,
  onBlur,
  subtext,
  isReadOnly,
  required,
  showError = false,
}: FormCheckboxProps) => {
  const handleChange = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : maxSelect && value.length >= maxSelect
        ? value
        : [...value, optionValue];
    onChange(newValue);
  };

  const isDisabled = (optionValue: string) => {
    if (!maxSelect) return false;
    return !value.includes(optionValue) && value.length >= maxSelect;
  };

  return (
    <FormControl
      component="fieldset"
      margin="normal"
      error={showError && required && (!value || value.length === 0)}
      sx={{ gap: "4px", marginTop: "0" }}
    >
      <Typography variant="h5">
        {text}
        {required && <span style={{ color: "#C14743" }}>*</span>}
      </Typography>
      {subtext && <Typography variant="bodySmall">{subtext}</Typography>}
      <FormGroup onBlur={onBlur}>
        {options.map((option: Option) => (
          <FormControlLabel
            key={option.value}
            control={
              <Checkbox
                checked={value.includes(option.value)}
                onChange={() => handleChange(option.value)}
                disabled={isDisabled(option.value) || isReadOnly}
              />
            }
            label={<Typography variant="bodySmall">{option.label}</Typography>}
          />
        ))}
      </FormGroup>
    </FormControl>
  );
};

interface FormLocationProps extends Omit<LocationField, "type"> {
  onChange: (value: Location) => void;
  onBlur?: () => void;
  isReadOnly: boolean;
  showError?: boolean;
}

const FormLocation = ({
  text,
  value,
  onChange,
  onBlur,
  subtext,
  isReadOnly,
  required,
  showError = false,
}: FormLocationProps) => {
  return (
    <FlexBox direction="column" sx={{ gap: "4px", marginTop: "0" }}>
      {text && (
        <Typography variant="h5">
          {text}
          {required && <span style={{ color: "#C14743" }}>*</span>}
        </Typography>
      )}
      {subtext && <Typography variant="bodySmall">{subtext}</Typography>}
      <AddressAutocomplete
        label=""
        restrictToAddresses={false}
        setValue={onChange}
        initialValue={value?.formattedAddress || ""}
        disabled={isReadOnly}
        onBlur={onBlur}
        error={
          showError && required && !value ? "This field is required" : undefined
        }
        inputSx={{
          marginTop: "8px",
          input: {
            marginTop: "0",
            ...(isReadOnly && {
              "&.Mui-disabled": {
                WebkitTextFillColor: "rgba(0, 0, 0, 0.7)",
              },
            }),
          },
        }}
      />
    </FlexBox>
  );
};

interface FormAttachmentProps extends Omit<AttachmentField, "type"> {
  onChange: (value: PlainMessage<UploadAttachment>[]) => void;
  onBlur?: () => void;
  isReadOnly: boolean;
  required?: boolean;
  showError?: boolean;
}

const FormAttachment = ({
  text,
  subtext,
  value = [],
  onChange,
  onBlur,
  isReadOnly,
  required = false,
  maxFiles = 5,
}: FormAttachmentProps) => {
  const [hover, setHover] = useState(false);

  const { onUpload, fileUploads, removeUpload, uploadsInProgress } =
    useUploader({
      initialAttachments: value,
      maxUploadBytes: 10000000, // 10MB
      maxUploadBytesNonImage: 5000000, // 5MB for non-images
    });

  const setAttachments = React.useCallback(
    (attachments: PlainMessage<UploadAttachment>[]) => {
      onChange(attachments);
      onBlur?.();
    },
    [onChange, onBlur],
  );

  const handleUpload = (files: FileList | File[] | null) => {
    if (isReadOnly || !files) return;

    const totalFiles = fileUploads.length + files.length;
    if (totalFiles > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    onUpload(files, undefined, setAttachments);
  };

  const handleRemove = (filename: string) => {
    if (isReadOnly) return;

    removeUpload(filename, setAttachments);
  };

  if (isReadOnly && fileUploads.length === 0) {
    return (
      <Box>
        <Typography
          variant="bodyHeavy"
          sx={{ mb: subtext ? 0.5 : 2, fontSize: "16px" }}
        >
          {text}
          {required && <span style={{ color: "red" }}> *</span>}
        </Typography>
        {subtext && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtext}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          No attachments
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="bodyHeavy"
        sx={{ mb: subtext ? 0.5 : 2, fontSize: "16px" }}
      >
        {text}
        {required && <span style={{ color: "red" }}> *</span>}
      </Typography>
      {subtext && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtext}
        </Typography>
      )}

      {!isReadOnly && (
        <DragNDrop
          enabled={!uploadsInProgress}
          setHover={setHover}
          onUpload={handleUpload}
        >
          <Box
            sx={{
              border: "2px dashed #D0D5DD",
              borderRadius: "8px",
              padding: "24px",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: hover ? "#f8f9fa" : "transparent",
              "&:hover": {
                backgroundColor: "#f8f9fa",
                borderColor: "#98A2B3",
              },
            }}
            onClick={() => {
              const input = document.createElement("input");
              input.type = "file";
              input.multiple = true;
              input.onchange = (e) => {
                const target = e.target as HTMLInputElement;
                if (target.files) {
                  handleUpload(target.files);
                }
              };
              input.click();
            }}
          >
            <Paperclip
              size={32}
              color="#667085"
              style={{ marginBottom: "8px" }}
            />
            <Typography variant="body1" color="text.secondary">
              Drop files here or click to upload
            </Typography>
            <Typography variant="body2" color="text.tertiary">
              Max {maxFiles} files, up to 10MB each
            </Typography>
          </Box>
        </DragNDrop>
      )}

      {fileUploads.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            marginTop: !isReadOnly ? "16px" : "0px",
          }}
        >
          {fileUploads.map((file, index) => (
            <UploadThumbnail
              key={`${file.filename}-${index}`}
              file={file}
              removeUpload={isReadOnly ? () => {} : handleRemove}
              canOpen={true}
            />
          ))}
        </Box>
      )}

      {uploadsInProgress && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Uploading files...
        </Typography>
      )}
    </Box>
  );
};

interface FormRendererProps {
  form: FormStructure;
  onFieldChange: (fieldId: string, value: any) => void;
  permission?: Permission;
  onSubmit?: () => void;
  mode?: "create" | "view" | "complete";
  viewMode?: "edit" | "view" | "results";
  setViewMode?: (mode: "edit" | "view" | "results") => void;
  uuid: string;
  additionalOptions?: React.ReactNode;
  submitter?: AccountStub;
  submitterDate?: Date;
  document?: Document;
}

export const FormRenderer = ({
  form,
  onFieldChange,
  permission,
  onSubmit,
  mode,
  viewMode,
  setViewMode,
  uuid,
  additionalOptions,
  submitter,
  submitterDate,
  document,
}: FormRendererProps) => {
  const isReadOnly = mode === "view" || permission === Permission.READ;
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const { pushSnack } = useSnackpack();
  const currentUser = useContext(CurrentUserContext);
  const navigate = useNavigate();
  const { request: copyDocumentRequest } = useCopyDocument();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const successDialog = useConfirmationDialog();

  // Pre-fill form fields from query parameters on mount
  useEffect(() => {
    if (isReadOnly) return;

    const prefillValues: Record<string, any> = {};

    // Build a map of query param key (lowercase) to field
    searchParams.forEach((value, key) => {
      const decodedKey = decodeURIComponent(key).toLowerCase();
      const decodedValue = decodeURIComponent(value);

      // Find matching field by id or text
      const matchingField = form.fields.find(
        (field) =>
          field.id.toLowerCase() === decodedKey ||
          field.text.toLowerCase() === decodedKey,
      );

      if (matchingField && !matchingField.value) {
        // Only prefill if field is currently empty
        if (matchingField.type === "text") {
          prefillValues[matchingField.id] = decodedValue;
        } else if (matchingField.type === "radio") {
          // Check if the value matches one of the options
          const matchingOption = matchingField.options.find(
            (opt) =>
              opt.value.toLowerCase() === decodedValue.toLowerCase() ||
              opt.label.toLowerCase() === decodedValue.toLowerCase(),
          );
          if (matchingOption) {
            prefillValues[matchingField.id] = matchingOption.value;
          }
        } else if (matchingField.type === "checkbox") {
          // Parse comma-separated values for checkboxes
          const values = decodedValue.split(",").map((v) => v.trim());
          const matchingValues = values
            .map((val) => {
              const matchingOption = matchingField.options.find(
                (opt) =>
                  opt.value.toLowerCase() === val.toLowerCase() ||
                  opt.label.toLowerCase() === val.toLowerCase(),
              );
              return matchingOption?.value;
            })
            .filter((v): v is string => v !== undefined);

          if (matchingValues.length > 0) {
            prefillValues[matchingField.id] = matchingValues;
          }
        }
        // Note: location and attachment types are not pre-filled from query params
      }
    });

    // Apply prefill values
    Object.entries(prefillValues).forEach(([fieldId, value]) => {
      onFieldChange(fieldId, value);
    });
  }, [form.fields, searchParams, onFieldChange, isReadOnly]);

  const handleFieldBlur = (fieldId: string) => {
    setTouchedFields((prev) => new Set([...Array.from(prev), fieldId]));
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit();
      successDialog.openDialog(() => {
        successDialog.closeDialog();
        const nav = (window as any).navigation;
        const prevEntry = nav?.entries()[nav.currentIndex - 1];
        if (prevEntry?.url) {
          window.location.href = prevEntry.url;
        } else {
          window.addEventListener("popstate", () => window.location.reload(), {
            once: true,
          });
          navigate(-1);
        }
      });
    }
  };

  const isFormValid = () => {
    return form.fields.every((field) => {
      if (!field.required) return true;

      if (field.type === "text") {
        return field.value && field.value.trim() !== "";
      } else if (field.type === "radio") {
        return field.value && field.value !== "";
      } else if (field.type === "checkbox") {
        return (
          field.value && Array.isArray(field.value) && field.value.length > 0
        );
      } else if (field.type === "location") {
        return field.value && field.value.formattedAddress;
      } else if (field.type === "attachment") {
        return (
          field.value && Array.isArray(field.value) && field.value.length > 0
        );
      }
      return true;
    });
  };

  const handleCloneForm = async () => {
    if (!document?.sourceDocumentRef || !currentUser) return;

    const result = await copyDocumentRequest(
      new CopyDocumentRequest({
        fromDocumentType: DocumentType.FORM,
        fromEntityType: "advisor",
        fromEntityRef: document.sourceDocumentRef,
        toDocumentType: DocumentType.FORM,
        toEntityType: "advisor",
        toEntityRef: currentUser.ref,
        creationSourceName: "form",
        title: form.title ? `Copy of ${form.title}` : "Copy of Form",
        replaceTitle: true,
      }),
    );

    if (result?.document?.uuid) {
      pushSnack({
        message: "Form copied successfully!",
        alertSeverity: "success",
        key: "form-clone-success",
      });
      navigate(
        `/forms/create/${encodeURIComponent(result.document.uuid)}?created=1`,
      );
    }
  };

  const shouldShowCloneButton =
    mode === "complete" &&
    currentUser &&
    isUserAdvisor(currentUser) &&
    document?.sourceDocumentRef &&
    document?.creationSource === "form";

  const cloneButton = shouldShowCloneButton ? (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Button
        variant="text"
        startIcon={<Copy size={18} />}
        onClick={handleCloneForm}
        sx={{ flexShrink: 0, height: "unset" }}
      >
        Copy this form
      </Button>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: "12px" }}
      >
        Edit or share with your own clients.
      </Typography>
    </Box>
  ) : null;

  const renderField = (field: FormField) => {
    const filteredField =
      field.type === "checkbox" || field.type === "radio"
        ? {
            ...field,
            options: field.options.filter(
              (option) => option.label.trim() !== "",
            ),
          }
        : field;

    const showError = touchedFields.has(field.id);

    switch (filteredField.type) {
      case "text":
        return (
          <FormTextField
            {...filteredField}
            onChange={(value) => onFieldChange(field.id, value)}
            onBlur={() => handleFieldBlur(field.id)}
            isReadOnly={isReadOnly}
            showError={showError}
          />
        );
      case "radio":
        return (
          <FormRadio
            {...filteredField}
            onChange={(value) => onFieldChange(field.id, value)}
            onBlur={() => handleFieldBlur(field.id)}
            isReadOnly={isReadOnly}
            showError={showError}
          />
        );
      case "checkbox":
        return (
          <FormCheckbox
            {...filteredField}
            onChange={(value) => onFieldChange(field.id, value)}
            onBlur={() => handleFieldBlur(field.id)}
            isReadOnly={isReadOnly}
            showError={showError}
          />
        );
      case "location":
        return (
          <FormLocation
            {...filteredField}
            onChange={(value) => onFieldChange(field.id, value)}
            onBlur={() => handleFieldBlur(field.id)}
            isReadOnly={isReadOnly}
            showError={showError}
          />
        );
      case "attachment":
        return (
          <FormAttachment
            {...filteredField}
            onChange={(newValue) => onFieldChange(field.id, newValue)}
            onBlur={() => handleFieldBlur(field.id)}
            isReadOnly={isReadOnly}
            required={filteredField.required || false}
            showError={showError}
          />
        );
      default:
        return null;
    }
  };

  return (
    <FlexBox direction="column" alignItems="unset" gap="24px">
      <FormHeader
        title={form.title}
        description={form.description}
        uuid={uuid}
        editable={false}
        showViewModeToggle={mode === "create" && !!viewMode && !!setViewMode}
        viewMode={viewMode}
        setViewMode={setViewMode}
        additionalOptions={
          <FlexBox direction="row" gap="8px" alignItems="center">
            {!isMobile && cloneButton}
            {additionalOptions}
          </FlexBox>
        }
        showCopyLink={mode === "create" || (mode === "view" && !!viewMode)}
        submitter={submitter}
        submitterDate={submitterDate}
      />

      <Divider />

      <Box
        component="form"
        noValidate
        autoComplete="off"
        sx={{ width: "100%" }}
      >
        {form.fields.map((field) => (
          <Box key={field.id} sx={{ mb: 3 }}>
            {renderField(field)}
          </Box>
        ))}

        {onSubmit && (
          <Box
            sx={{
              mt: 4,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!isFormValid() || isReadOnly}
              size="large"
            >
              Submit
            </Button>
          </Box>
        )}
      </Box>
      <ConfirmationDialog
        title="Success"
        content="Form submitted successfully!"
        noIcon={true}
        yesButtonText="OK"
        hideCancelButton={true}
        {...successDialog.dialogProps}
      />
    </FlexBox>
  );
};
